import { useInView } from 'react-intersection-observer'
import { useState } from 'react'
import { motion } from 'framer-motion'
import { HiExternalLink, HiBadgeCheck } from 'react-icons/hi'
import { FiEye } from 'react-icons/fi'
import type { Language } from '../App'
import { t } from '../utils/translations'
import { certificates } from '../utils/data'
import CertificateViewer from './CertificateViewer'
import './Certificates.css'

interface CertificatesProps { language: Language }

type CertType = (typeof certificates)[number]

export default function Certificates({ language }: CertificatesProps) {
  const [ref, inView] = useInView({ threshold: 0.1, triggerOnce: true })
  const [selected, setSelected] = useState<CertType | null>(null)

  return (
    <section id="certificates" className="section-alt" ref={ref}>
      <div className="orb orb-purple cert-orb" />
      <div className="container">
        <motion.div
          className="section-header"
          initial={{ y: 30, opacity: 0 }}
          animate={inView ? { y: 0, opacity: 1 } : {}}
          transition={{ duration: 0.6 }}
        >
          <p className="section-label">{t('cert.label', language)}</p>
          <h2 className="section-title">{t('cert.title', language)}</h2>
          <div className="gradient-divider" />
          <p className="section-desc">{t('cert.desc', language)}</p>
        </motion.div>

      <div className="cert-grid">
          {certificates.map((cert, i) => (
            <motion.div
              key={cert.id}
              className="cert-card card"
              initial={{ y: 20, opacity: 0, scale: 0.95 }}
              animate={inView ? { y: 0, opacity: 1, scale: 1 } : {}}
              transition={{ duration: 0, delay:  0, }}
              whileHover={{ y: -14 }}
            >
              <div
                className="cert-glow"
                style={{ background: `radial-gradient(circle at center, ${cert.color}18 0%, transparent 70%)` }}
              />

              <div className="cert-top">
                <div
                  className="cert-icon"
                  
                >
                  <span>{cert.icon}</span>
                </div>
                <HiBadgeCheck className="cert-verified" style={{ color: cert.color }} />
              </div>

              <div className="cert-body">
                <h1 className="cert-title">{cert.title}</h1>
                <p className="cert-issuer" style={{ color: cert.color }}>{cert.issuer}</p>
                <div className="cert-meta">
                  <span className="cert-date">{cert.date}</span>
                  {/* <span className="cert-id">ID: {cert.credentialId}</span> */}
                </div>

                {/* File availability indicator */}
                <div className="cert-file-status">
                  {cert.localFile ? (
                    <span className="cert-file-badge cert-file-badge--local">
                      <span className="cert-file-dot cert-file-dot--green" />
                      {/* Local file ready */}
                    </span>
                  ) : (
                    <span className="cert-file-badge cert-file-badge--missing">
                      <span className="cert-file-dot cert-file-dot--yellow" />
                      No local file — drop in <code>public/images/certificates/</code>
                    </span>
                  )}
                </div>
              </div>

              {/* View Certificate button — opens modal */}
              <button
                className="cert-btn btn btn-ghost"
                onClick={() => setSelected(cert)}
                style={{ '--accent': cert.color } as React.CSSProperties}
              >
                <FiEye />
                {t('cert.view', language)}
              </button>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Certificate viewer modal */}
      {selected && (
        <CertificateViewer
          isOpen={Boolean(selected)}
          onClose={() => setSelected(null)}
          title={selected.title}
          issuer={selected.issuer}
          localFile={selected.localFile}
          credentialUrl={selected.credentialUrl}
          color={selected.color}
        />
      )}
    </section>
  )
}
